import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Curso } from 'models/curso_model';
import {
  getCursos,
  getAllCursos,
  getCursoById,
  createCurso,
  editCurso,
  deleteCurso,
  getReporteSemanalCursos,
} from 'repositories/curso_repository';

interface CursoState {
  cursos: Curso[];
  curso: Curso | null;
  stepForm: number;
  formCurso: Partial<Curso>;
  loading: boolean;
  error: string | null;
}

const initialState: CursoState = {
  cursos: [],
  curso: null,
  stepForm: 1,
  formCurso: {},
  loading: false,
  error: null,
};

export const cargarAllCursos = createAsyncThunk<Curso[], { profesor?: boolean } | undefined, { rejectValue: string }>(
  'curso/cargarAllCursos',
  async (filters = {}, { rejectWithValue }) => {
    try {
      return ((await getAllCursos(filters)) ?? []) as Curso[];
    } catch {
      return rejectWithValue('Error al cargar cursos');
    }
  },
);

export const cargarCursos = createAsyncThunk<Curso[], { cursoFilter?: string; turnoFilter?: string; gestionFilter?: string } | undefined, { rejectValue: string }>(
  'curso/cargarCursos',
  async (
    filters = {},
    { rejectWithValue },
  ) => {
    try {
      return ((await getCursos(filters.cursoFilter, filters.turnoFilter, filters.gestionFilter)) ?? []) as Curso[];
    } catch {
      return rejectWithValue('Error al cargar cursos');
    }
  },
);

export const cargarCursoPorId = createAsyncThunk(
  'curso/cargarCursoPorId',
  async (id: string, { rejectWithValue }) => {
    try {
      return await getCursoById(id);
    } catch {
      return rejectWithValue('Error al cargar curso');
    }
  },
);

export const agregarCurso = createAsyncThunk(
  'curso/agregarCurso',
  async (data: Curso, { rejectWithValue }) => {
    try {
      await createCurso(data);
    } catch {
      return rejectWithValue('Error al agregar curso');
    }
  },
);

export const editarCurso = createAsyncThunk(
  'curso/editarCurso',
  async ({ data, cursoId }: { data: Curso; cursoId: string }, { rejectWithValue }) => {
    try {
      await editCurso(data, cursoId);
    } catch {
      return rejectWithValue('Error al editar curso');
    }
  },
);

export const eliminarCurso = createAsyncThunk(
  'curso/eliminarCurso',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteCurso(id);
    } catch {
      return rejectWithValue('Error al eliminar curso');
    }
  },
);

export const reporteSemanalCursos = createAsyncThunk(
  'curso/reporteSemanal',
  async ({ idCurso, fecha }: { idCurso: string; fecha: string }, { rejectWithValue }) => {
    try {
      return await getReporteSemanalCursos(idCurso, fecha);
    } catch {
      return rejectWithValue('Error al obtener reporte semanal');
    }
  },
);

export const cursoSlice = createSlice({
  name: 'curso',
  initialState,
  reducers: {
    changeCurso: (state, action: PayloadAction<Curso | null>) => {
      state.curso = action.payload;
    },
    setStepForm: (state, action: PayloadAction<number>) => {
      state.stepForm = action.payload;
    },
    setFormCurso: (state, action: PayloadAction<Partial<Curso>>) => {
      state.formCurso = { ...state.formCurso, ...action.payload };
    },
    setCursos: (state, action: PayloadAction<Curso[]>) => {
      state.cursos = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const setCursosReducer = (state: CursoState, action: { payload: Curso[] }) => {
      state.loading = false;
      state.cursos = action.payload;
    };
    builder
      .addCase(cargarAllCursos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarAllCursos.fulfilled, setCursosReducer)
      .addCase(cargarAllCursos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cargarCursos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarCursos.fulfilled, setCursosReducer)
      .addCase(cargarCursos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cargarCursoPorId.fulfilled, (state, action) => {
        state.curso = action.payload as Curso;
      });
  },
});

export const { changeCurso, setStepForm, setFormCurso, setCursos, clearError: clearCursoError } =
  cursoSlice.actions;
export default cursoSlice.reducer;
